import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { healthcareInvoicesApi, patientsApi, postVisitTasksApi } from '../services/api';
import './clinic.css';
import { italianDay, loadStudioAppointments } from '../services/studioAgenda';

const aiExamples = [
  'Riepiloga le priorità dello studio di oggi.',
  'Mostrami i piani alimentari da consegnare prima.',
  'Controlla fatture, saldo e Sistema TS aperti.'
];

const directActions = [
  { to: '/patients', state: { openCreate: true }, icon: 'patient', title: 'Nuovo paziente', text: 'Crea cartella e prima visita' },
  { to: '/visits/new', icon: 'agenda', title: 'Avvia visita', text: 'Apri il flusso clinico' },
  { to: '/bia', icon: 'bia', title: 'Nuova BIA', text: 'Acquisisci o registra' },
  { to: '/diets', icon: 'plan', title: 'Piani', text: 'Modelli e proposte' }
];

const hiddenTaskStatuses = new Set(['done', 'cancelled']);
const openTsStates = new Set(['not_ready', 'awaiting_payment', 'pending', 'validated', 'validation_error', 'uncertain']);

function DashboardActionIcon({ name }) {
  const paths = {
    agenda: <><path d="M7 7.5h10"/><path d="M8.5 4.5v4"/><path d="M15.5 4.5v4"/><path d="M5.5 8.5h13"/><rect x="5" y="6" width="14" height="13" rx="2.5"/><path d="M8.5 12h2.5M13 12h2.5M8.5 15.5H11"/></>,
    arrow: <><path d="M7 12h10"/><path d="m13 8 4 4-4 4"/></>,
    patient: <><circle cx="12" cy="8.5" r="3"/><path d="M6.5 18.5c1-3 3-4.5 5.5-4.5s4.5 1.5 5.5 4.5"/><path d="M18 9v4M16 11h4"/></>,
    bia: <><path d="M5.5 15.5h13"/><path d="M7 12.5c1.4-3.2 3-4.8 5-4.8s3.6 1.6 5 4.8"/><path d="M12 7.7l2.2 4.1"/><circle cx="12" cy="12.5" r=".8"/></>,
    plan: <><path d="M7.5 5.5h7.5l2.5 2.5v10.5h-10z"/><path d="M15 5.5V8h2.5"/><path d="M9.5 11h5M9.5 14h5M9.5 17h3"/></>,
    receipt: <><path d="M7 5.5h10v13l-2-1.2-2 1.2-2-1.2-2 1.2z"/><path d="M9.5 9h5M9.5 12h5M9.5 15h3"/></>,
    check: <><path d="m7 12.5 3 3 7-7"/><circle cx="12" cy="12" r="8"/></>,
    clock: <><circle cx="12" cy="12" r="8"/><path d="M12 7.5V12l3 2"/></>
  };
  return <svg className={`dashboard-action-icon dashboard-action-icon-${name}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false">{paths[name]}</svg>;
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseDateOnly(value) {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateLabel(value, options = { dateStyle: 'medium' }) {
  const date = value instanceof Date ? value : parseDate(value);
  return date ? date.toLocaleString('it-IT', { timeZone: 'Europe/Rome', ...options }) : 'Da definire';
}

function dateOnlyLabel(value) {
  const date = parseDateOnly(value);
  return date ? date.toLocaleDateString('it-IT') : 'Da definire';
}

function daysFromToday(date) {
  if (!date) return null;
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((target - start) / 86400000);
}

function dueTone(date) {
  const distance = daysFromToday(date);
  if (distance === null) return 'neutral';
  if (distance < 0) return 'danger';
  if (distance <= 1) return 'urgent';
  if (distance <= 3) return 'soon';
  return 'neutral';
}

function urgencyCopy(date, noun = 'Scadenza') {
  const distance = daysFromToday(date);
  if (distance === null) return `${noun} da definire`;
  if (distance < 0) return `In ritardo di ${Math.abs(distance)} ${Math.abs(distance) === 1 ? 'giorno' : 'giorni'}`;
  if (distance === 0) return 'Da fare oggi';
  if (distance === 1) return 'Da fare domani';
  return `${noun} tra ${distance} giorni`;
}

function invoiceNeedsAttention(invoice) {
  if (!invoice) return false;
  if (invoice.status === 'draft') return true;
  if (invoice.status === 'issued' && !invoice.paidOn) return true;
  return openTsStates.has(invoice.tsState) && invoice.tsState !== 'submitted';
}

function invoiceLabel(invoice) {
  if (invoice.status === 'draft') return 'Fattura da emettere';
  if (invoice.status === 'issued' && !invoice.paidOn) return 'Saldo da registrare';
  if (invoice.tsState === 'pending' || invoice.tsState === 'validated') return 'TS da inviare';
  if (invoice.tsState === 'validation_error' || invoice.tsState === 'uncertain') return 'TS da controllare';
  if (invoice.tsState === 'awaiting_payment') return 'Attende saldo';
  return 'Documento fiscale';
}

function buildAiSuggestions({ planTasks, controlTasks, fiscalTasks, today }) {
  const suggestions = [];
  const firstPlan = planTasks[0];
  const firstControl = controlTasks[0];
  const firstInvoice = fiscalTasks[0];
  const firstAppointment = today[0];

  if (firstPlan) {
    suggestions.push({
      icon: 'plan',
      title: 'Prepara il piano con Sibilla',
      text: `${firstPlan.patientName || 'Paziente'} · consegna ${dateOnlyLabel(firstPlan.dueOn)}`,
      prompt: `Prepara una bozza di piano alimentare per ${firstPlan.patientName || 'questo paziente'} partendo dall’ultima visita, dalla BIA e dalle note disponibili. Evidenzia cosa devo verificare prima di consegnarlo.`
    });
  }

  if (firstControl) {
    suggestions.push({
      icon: 'agenda',
      title: 'Prepara il controllo',
      text: `${firstControl.patientName || 'Paziente'} · ${dateLabel(firstControl.scheduledFor, { dateStyle: 'medium', timeStyle: 'short' })}`,
      prompt: `Prepara il controllo di ${firstControl.patientName || 'questo paziente'}: andamento clinico, BIA da confrontare, punti da chiedere e prossimi passi.`
    });
  }

  if (firstInvoice) {
    suggestions.push({
      icon: 'receipt',
      title: 'Chiudi amministrazione visita',
      text: `${firstInvoice.buyerName || 'Paziente'} · ${invoiceLabel(firstInvoice)}`,
      prompt: `Riepiloga cosa manca per chiudere fattura, saldo e Sistema TS per ${firstInvoice.buyerName || 'questo paziente'}. Dammi una lista operativa breve.`
    });
  }

  if (firstAppointment) {
    suggestions.push({
      icon: 'bia',
      title: 'Prepara la prossima visita',
      text: `${firstAppointment.displayName || 'Appuntamento'} · ${dateLabel(firstAppointment.startsAt, { timeStyle: 'short' })}`,
      prompt: `Prepara la visita di oggi per ${firstAppointment.displayName || 'il prossimo appuntamento'}: cosa controllare, quali dati clinici aprire e quali azioni ricordare a fine visita.`
    });
  }

  if (!suggestions.length) {
    suggestions.push({
      icon: 'check',
      title: 'Rivedi lo studio con Sibilla',
      text: 'Controllo generale di agenda, piani e documenti',
      prompt: 'Riepiloga la situazione dello studio: appuntamenti, piani alimentari, fatture, saldi e Sistema TS. Indicami solo le attività da fare.'
    });
  }

  return suggestions.slice(0, 3);
}

function DashboardLane({ title, subtitle, count, empty, children }) {
  return <section className="dashboard-lane" aria-labelledby={`${title.replaceAll(' ', '-')}-title`}>
    <div className="dashboard-lane-head">
      <div>
        <h2 id={`${title.replaceAll(' ', '-')}-title`}>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <span>{count}</span>
    </div>
    {count ? <div className="dashboard-lane-list">{children}</div> : <div className="dashboard-lane-empty">{empty}</div>}
  </section>;
}

function WorkItem({ to, icon, title, meta, status, tone = 'neutral' }) {
  return <Link className={`dashboard-work-item tone-${tone}`} to={to}>
    <span className="dashboard-work-icon"><DashboardActionIcon name={icon}/></span>
    <span className="dashboard-work-copy"><strong>{title}</strong><small>{meta}</small></span>
    <span className="dashboard-work-status">{status}</span>
  </Link>;
}

export function Dashboard() {
  const navigate = useNavigate();
  const commandRef = useRef(null);
  const [command, setCommand] = useState('');
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [postVisitTasks, setPostVisitTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const [patientData, appointmentData, taskData, invoiceData] = await Promise.all([
        patientsApi.getAll(),
        loadStudioAppointments(now),
        postVisitTasksApi.getAll({ limit: 80 }),
        healthcareInvoicesApi.list()
      ]);
      setPatients(patientData || []);
      setAppointments(appointmentData || []);
      setPostVisitTasks(taskData || []);
      setInvoices(invoiceData || []);
      setHasLoaded(true);
    } catch (requestError) {
      setError(requestError.message || 'Non è stato possibile aggiornare la panoramica.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => { if (!document.hidden) load(); };
    const timer = setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [load]);

  const today = appointments.filter(item => italianDay(item.startsAt) === italianDay(new Date()));
  const openPostVisitTasks = postVisitTasks.filter(task => !hiddenTaskStatuses.has(task.status));
  const planTasks = openPostVisitTasks.filter(task => task.kind === 'diet-plan-delivery');
  const controlTasks = openPostVisitTasks.filter(task => task.kind === 'follow-up-appointment');
  const fiscalTasks = invoices.filter(invoiceNeedsAttention);
  const issuedButUnpaid = invoices.filter(invoice => invoice.status === 'issued' && !invoice.paidOn);
  const tsToCheck = invoices.filter(invoice => invoice.status === 'issued' && invoice.paidOn && openTsStates.has(invoice.tsState) && invoice.tsState !== 'submitted');

  const priority = useMemo(() => {
    const plan = planTasks
      .map(task => ({ type: 'plan', task, date: parseDateOnly(task.dueOn), weight: 1 }))
      .sort((a, b) => (a.date?.getTime() || Number.MAX_SAFE_INTEGER) - (b.date?.getTime() || Number.MAX_SAFE_INTEGER))[0];
    if (plan) return plan;
    const fiscal = fiscalTasks[0];
    if (fiscal) return { type: 'fiscal', invoice: fiscal, weight: 2 };
    const control = controlTasks
      .map(task => ({ type: 'control', task, date: parseDate(task.scheduledFor), weight: 3 }))
      .sort((a, b) => (a.date?.getTime() || Number.MAX_SAFE_INTEGER) - (b.date?.getTime() || Number.MAX_SAFE_INTEGER))[0];
    if (control) return control;
    const appointment = today.map(item => ({ type: 'appointment', appointment: item, date: parseDate(item.startsAt), weight: 4 }))[0];
    return appointment || null;
  }, [controlTasks, fiscalTasks, planTasks, today]);

  const startWithSibilla = event => {
    event.preventDefault();
    const initialMessage = command.trim();
    if (!initialMessage) {
      commandRef.current?.focus();
      return;
    }
    navigate('/copilot', { state: { initialMessage, autoSubmit: true } });
  };

  const chooseExample = example => {
    setCommand(example);
    commandRef.current?.focus();
  };

  const launchSuggestion = prompt => {
    navigate('/copilot', { state: { initialMessage: prompt, autoSubmit: true } });
  };

  const priorityTitle = !priority ? 'Lo studio è in pari' : priority.type === 'plan'
    ? 'Produci il piano alimentare'
    : priority.type === 'fiscal'
      ? invoiceLabel(priority.invoice)
      : priority.type === 'control'
        ? 'Conferma il prossimo controllo'
        : 'Prepara la prossima visita';
  const priorityText = !priority ? 'Non risultano lavori clinici aperti nella coda post-visita.' : priority.type === 'plan'
    ? `${priority.task.patientName || 'Paziente'} aspetta il piano. ${urgencyCopy(priority.date, 'Consegna')}.`
    : priority.type === 'fiscal'
      ? `${priority.invoice.buyerName || 'Paziente'} · ${Number(priority.invoice.total || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}.`
      : priority.type === 'control'
        ? `${priority.task.patientName || 'Paziente'} · ${dateLabel(priority.task.scheduledFor, { dateStyle: 'medium', timeStyle: 'short' })}.`
        : `${priority.appointment.displayName || 'Appuntamento'} · ${dateLabel(priority.appointment.startsAt, { timeStyle: 'short' })}.`;
  const priorityTo = !priority ? '/patients' : priority.type === 'fiscal'
    ? `/patients/${priority.invoice.patientId}/visits/${priority.invoice.visitId}`
    : priority.type === 'appointment'
      ? '/agenda'
      : `/patients/${priority.task.patientId}/visits/${priority.task.visitId}`;

  const aiSuggestions = buildAiSuggestions({ planTasks, controlTasks, fiscalTasks, today });

  return <main className="clinic-page dashboard-state-surface dashboard-ai-page dashboard-workbench" aria-busy={loading}>
    <section className="dashboard-command-center" aria-labelledby="dashboardTodayTitle">
      <div className="dashboard-priority-card">
        <div className="dashboard-priority-copy">
          <span className="dashboard-priority-label">Prossima cosa da fare</span>
          <h1 id="dashboardTodayTitle">{loading && !hasLoaded ? 'Sto leggendo lo studio…' : priorityTitle}</h1>
          <p>{loading && !hasLoaded ? 'Aggiorno agenda, lavori post-visita e documenti fiscali.' : priorityText}</p>
        </div>
        <Link className="dashboard-primary-action" to={priorityTo}>{priority ? 'Apri attività' : 'Apri archivio'}<DashboardActionIcon name="arrow"/></Link>
      </div>
      <form className="dashboard-mini-copilot" onSubmit={startWithSibilla} aria-labelledby="dashboardAiTitle">
        <h2 id="dashboardAiTitle">Chiedi a Sibilla</h2>
        <textarea ref={commandRef} value={command} onChange={event => setCommand(event.target.value)} placeholder="Es. Prepara il piano per il paziente appena visitato…" rows="3" maxLength="4000"/>
        <div className="dashboard-ai-suggestions" aria-label="Suggerimenti operativi di Sibilla">
          {aiSuggestions.map(suggestion => <button key={suggestion.title} type="button" onClick={() => launchSuggestion(suggestion.prompt)}>
            <span className="dashboard-ai-suggestion-icon"><DashboardActionIcon name={suggestion.icon}/></span>
            <span><strong>{suggestion.title}</strong><small>{suggestion.text}</small></span>
          </button>)}
        </div>
        <div className="dashboard-ai-examples" aria-label="Richieste di esempio">
          {aiExamples.map((example, index) => <button key={example} type="button" onClick={() => chooseExample(example)}><span>{index + 1}</span>{example}</button>)}
        </div>
        <button type="submit" disabled={!command.trim()}>Prepara richiesta libera</button>
      </form>
    </section>

    {error && <div className="dashboard-state-error" role="alert"><div><strong>Panoramica non aggiornata</strong><span>{error} I dati mostrati potrebbero non essere aggiornati.</span></div><button type="button" onClick={load} disabled={loading}>{loading ? 'Nuovo tentativo…' : 'Riprova'}</button></div>}

    <section className="dashboard-context dashboard-context-workload" aria-label="Carico operativo dello studio" aria-live="polite">
      {loading && !hasLoaded ? [0, 1, 2, 3].map(index => <div className="dashboard-context-item dashboard-state-skeleton" key={index}><span/><strong/><small/></div>)
        : <>
          <div className="dashboard-context-item"><span>Piani</span><strong>{planTasks.length}</strong><small>{planTasks.length === 1 ? 'da produrre' : 'da produrre'}</small></div>
          <div className="dashboard-context-item"><span>Controlli</span><strong>{controlTasks.length}</strong><small>da gestire</small></div>
          <div className="dashboard-context-item"><span>Fatture / TS</span><strong>{fiscalTasks.length}</strong><small>{issuedButUnpaid.length} saldo · {tsToCheck.length} TS</small></div>
          <div className="dashboard-context-item"><span>Oggi</span><strong>{today.length}</strong><small>{patients.length} pazienti attivi</small></div>
        </>}
    </section>

    <section className="dashboard-lanes" aria-label="Attività operative">
      <DashboardLane title="Piani da produrre" subtitle="Pazienti che aspettano il piano alimentare dopo la visita." count={planTasks.length} empty="Nessun piano in attesa di produzione.">
        {planTasks.slice(0, 6).map(task => {
          const due = parseDateOnly(task.dueOn);
          return <WorkItem key={task.id} to={`/patients/${task.patientId}/visits/${task.visitId}`} icon="plan" title={task.patientName || 'Paziente'} meta={`Consegna ${dateOnlyLabel(task.dueOn)}`} status={urgencyCopy(due, 'Consegna')} tone={dueTone(due)}/>;
        })}
      </DashboardLane>

      <DashboardLane title="Controlli da confermare" subtitle="Prossimi appuntamenti generati alla chiusura della visita." count={controlTasks.length} empty="Nessun controllo post-visita da gestire.">
        {controlTasks.slice(0, 6).map(task => <WorkItem key={task.id} to={`/patients/${task.patientId}/visits/${task.visitId}`} icon="agenda" title={task.patientName || 'Paziente'} meta={dateLabel(task.scheduledFor, { dateStyle: 'medium', timeStyle: 'short' })} status={task.statusLabel || 'In agenda'} tone="soon"/>)}
      </DashboardLane>

      <DashboardLane title="Fatture e TS" subtitle="Documenti sanitari da emettere, incassare o trasmettere." count={fiscalTasks.length} empty="Nessun documento fiscale aperto.">
        {fiscalTasks.slice(0, 6).map(invoice => <WorkItem key={invoice.id} to={`/patients/${invoice.patientId}/visits/${invoice.visitId}`} icon="receipt" title={invoice.buyerName || 'Paziente'} meta={invoice.number ? `${invoice.series}/${invoice.number}/${invoice.year}` : 'Numero non emesso'} status={invoiceLabel(invoice)} tone={invoice.tsState === 'validation_error' || invoice.tsState === 'uncertain' ? 'danger' : 'urgent'}/>)}
      </DashboardLane>
    </section>

    <div className="clinic-grid two dashboard-secondary dashboard-bottom-grid">
      <section className="clinic-panel dashboard-today-panel">
        <div className="clinic-panel-head"><div><h2>Agenda di oggi</h2><p>Visite e controlli da preparare nelle prossime ore.</p></div><Link className="dashboard-panel-action" to="/agenda"><DashboardActionIcon name="agenda"/><span>Agenda</span></Link></div>
        {loading && !hasLoaded
          ? <div className="dashboard-state-loading" role="status">Sto caricando gli appuntamenti…</div>
          : today.length
            ? <div className="timeline-list">{today.slice(0, 6).map(item => <div className="timeline-row" key={item.id}><div><strong>{new Date(item.startsAt).toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit' })}</strong><br/><small>{item.durationMinutes || 55} min</small></div><div><strong>{item.displayName || (item.patient ? `${item.patient.lastName} ${item.patient.firstName}` : 'Appuntamento')}</strong><br/><small>{item.type}</small></div><span className="clinic-pill">{item.status}</span></div>)}</div>
            : <div className="clinic-empty">Nessun appuntamento in agenda oggi.</div>}
      </section>
      <section className="clinic-panel dashboard-direct-panel">
        <div className="clinic-panel-head"><div><h2>Azioni rapide</h2><p>Operazioni frequenti senza passare dall’assistente.</p></div></div>
        <div className="dashboard-action-grid">{directActions.map(action => <Link key={action.to} className="dashboard-action-tile" to={action.to} state={action.state}><span className="dashboard-action-mark"><DashboardActionIcon name={action.icon}/></span><span className="dashboard-action-copy"><strong>{action.title}</strong><small>{action.text}</small></span><DashboardActionIcon name="arrow"/></Link>)}</div>
      </section>
    </div>
  </main>;
}
